import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, tap, catchError, throwError, of, map } from 'rxjs';
import { TenantService } from './tenant.service';
import { StoreContextService } from './store-context.service';
import { ListCacheService } from './list-cache.service';
import { SupportDataService } from './support-data.service';
import { ApiLoggerService } from './api-logger.service';
import { LoginRequest, LoginResponse, TenantUser, ProfileUpdateRequest, PasswordUpdateRequest } from '../models/api.models';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private userSubject = new BehaviorSubject<TenantUser | null>(null);
  user$ = this.userSubject.asObservable();

  /** Base URL from TenantService, tenant identified via X-Tenant header */
  private get base(): string { return this.tenant.apiBaseUrl; }

  constructor(
    private http: HttpClient,
    private router: Router,
    private tenant: TenantService,
    // v1.6.318 — needed so clearAuth() can reset in-memory store context
    // (BehaviorSubject + _loaded flag) on logout. Without this, the
    // previous user's active store leaks into the next user's session.
    private storeCtx: StoreContextService,
    // v1.6.611 — Additional in-memory caches that survive route changes
    // and leak across tenant boundaries on logout/switch:
    //   listCache — cached LIST responses (products, customers, orders…)
    //   supportData — cached taxonomy (categories, tags, allergens, etc.)
    //   apiLogger — debug log buffer (admin tools can show prior tenant's URLs)
    // Without busting these here, the operator logs out, logs in as a
    // different tenant, and the cached entries serve stale (or worse,
    // wrong-tenant) data until natural cache expiry / hard refresh.
    private listCache: ListCacheService,
    private supportData: SupportDataService,
    private apiLogger: ApiLoggerService,
  ) {
    const stored = localStorage.getItem('hpm_user');
    if (stored) {
      try { this.userSubject.next(JSON.parse(stored)); } catch {}
    }
  }

  // ─── Token Management ──────────────────────────────

  getToken(): string | null {
    const token = localStorage.getItem('hpm_token');
    // Guard against stale "undefined" or "null" string tokens
    if (!token || token === 'undefined' || token === 'null' || token === 'Bearer undefined') {
      localStorage.removeItem('hpm_token');
      return null;
    }
    return token;
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && this.tenant.isSet;
  }

  private storeAuth(token: string, user: TenantUser): void {
    localStorage.setItem('hpm_token', token);
    localStorage.setItem('hpm_user', JSON.stringify(user));
    this.userSubject.next(user);
  }

  /**
   * v1.6.318 — Comprehensive storage wipe on logout.
   *
   * The previous implementation only cleared 4 specific keys, which
   * meant data from User A's session leaked into User B's session
   * when they logged in on the same device:
   *
   *   - `tenantLogoUrl`           (set by general-settings + header)
   *   - `hmp_openai_model`        (user's AI model preference)
   *   - `hmp_recipe_model`        (user's AI recipe model preference)
   *   - `sub_recipe_ids`          (tenant-scoped recipe IDs)
   *   - `wsw_<tenant>_*`          (wizard cache: data, completed, current_step)
   *   - `fdd_*`                   (LocalStorageService prefix, if used)
   *   - any other `hpm_*` aside from tenant + instances list
   *
   * v1.6.318 fix: clear ALL keys that hold user-scoped or tenant-scoped
   * data. PRESERVE only:
   *   - `hpm_tenant`        (tenant slug — survives logout per UX intent)
   *   - `hpm_instances`     (saved instance picker list — device-level memory)
   *   - `label_editor_unit` (UI preference, no data leak risk)
   *
   * Also clears sessionStorage entirely. Anything important in there
   * (chunk error sentinel) self-heals next visit.
   */
  clearAuth(): void {
    // Keys we KEEP across a logout — must NOT be wiped
    const KEEP_KEYS = new Set<string>([
      'hpm_tenant',          // tenant slug — keeps the user on the same instance
      'hpm_instances',       // saved instance picker list — device-level
      'label_editor_unit',   // UI preference (in/cm) — no data leak
    ]);
    try {
      // Snapshot keys first because we're about to mutate the keyspace
      const allKeys = Object.keys(localStorage);
      for (const k of allKeys) {
        if (!KEEP_KEYS.has(k)) {
          try { localStorage.removeItem(k); } catch { /* ignore quota / serialization edge */ }
        }
      }
      // Belt-and-braces: also clear sessionStorage. Anything important
      // in it (chunk-error sentinel) self-heals next visit.
      try { sessionStorage.clear(); } catch { /* ignore */ }
    } catch {
      // localStorage unavailable (private mode, disabled, etc.) — best-effort
      // clear via the explicit-key path so token/user are still nuked
      try { localStorage.removeItem('hpm_token');       } catch {}
      try { localStorage.removeItem('hpm_user');        } catch {}
      try { localStorage.removeItem('storeListCache');  } catch {}
      try { localStorage.removeItem('activeStoreId');   } catch {}
    }
    // v1.6.318 — reset in-memory state so the previous user's active
    // store doesn't leak into the next user's session before their
    // own /stores GET resolves.
    try { this.storeCtx.clear(); } catch {}
    // v1.6.611 — Bust the in-memory caches that survive route
    // changes. Without these, logging out + logging in as a different
    // tenant serves stale cached data from the previous session
    // (operator reported seeing old tenant's info until hard refresh).
    // Each call is in its own try/catch so a single failure can't
    // block the rest of the cleanup.
    try { this.listCache.bustAll(); } catch {}
    try { this.supportData.bustAll(); } catch {}
    try { this.apiLogger.clearLogs(); } catch {}
    this.userSubject.next(null);
  }

  // ─── Login ─────────────────────────────────────────
  // POST /v1/tenant/login
  login(credentials: LoginRequest): Observable<any> {
    const fd = new FormData();
    fd.append('email', credentials.email);
    fd.append('password', credentials.password);
    return this.http.post<any>(`${this.base}/v1/tenant/login`, fd).pipe(
      tap(res => {
        console.log('[AuthService] Login response:', JSON.stringify(res).substring(0, 500));
        // API may return { token, user } or { data: { token, user } } or { status, data: { token, user } }
        const payload = res.data || res;
        const token = payload.token || payload.access_token || res.token || res.access_token;
        const user = payload.user || res.user;
        console.log('[AuthService] Extracted token:', token ? token.substring(0, 20) + '...' : 'NONE');
        if (token && token !== 'undefined') {
          this.storeAuth(token, user || { name: 'Admin', email: credentials.email });
        } else {
          console.error('[AuthService] No token found in login response!', res);
        }
      }),
      catchError(err => { this.clearAuth(); return throwError(() => err); })
    );
  }

  // ─── Logout ────────────────────────────────────────
  // POST /v1/tenant/logout
  logout(): Observable<any> {
    return this.http.post(`${this.base}/v1/tenant/logout`, {}).pipe(
      tap(() => { this.clearAuth(); this.router.navigate(['/login']); }),
      catchError(() => { this.clearAuth(); this.router.navigate(['/login']); return of(null); })
    );
  }

  /**
   * v1.6.775 — Token-based login for super-admin impersonation.
   *
   * The super-admin panel issues an impersonation bearer token and
   * redirects the operator's browser to `/auth/impersonate?token=xxx`
   * on this dashboard. This method:
   *
   *   1. Clears any existing session FIRST (per spec — the previous
   *      user's session must not leak into the impersonated session).
   *      `clearAuth()` is comprehensive (v1.6.318) — it preserves only
   *      the tenant slug, instance list, and a UI preference, wiping
   *      every other localStorage key plus all in-memory caches.
   *   2. Stores the new token under the same key the rest of the
   *      dashboard reads (`hpm_token`).
   *   3. Hydrates the user profile via `GET /v1/tenant/profile` so
   *      `user$` is populated and the AuthGuard lets the impersonator
   *      into protected routes.
   *
   * On any error (network, 401, malformed response) the half-applied
   * session is wiped via clearAuth() so the operator isn't left in a
   * half-authenticated state. The caller is responsible for the
   * post-success redirect.
   */
  loginWithToken(token: string): Observable<TenantUser> {
    // Step 1 — wipe any previous session.
    this.clearAuth();
    // Step 2 — install the new token. Done as a direct write rather
    // than through storeAuth() because we don't yet have a user object
    // (the profile call hydrates it).
    try { localStorage.setItem('hpm_token', token); } catch {}
    // Step 3 — fetch the user. getProfile() already writes hpm_user
    // and pushes onto userSubject on success.
    return this.getProfile().pipe(
      catchError(err => {
        // Leave no half-state. The token we just installed is bad
        // (expired, wrong tenant, etc.) — wipe and re-throw so the
        // caller can show an error.
        this.clearAuth();
        return throwError(() => err);
      })
    );
  }

  /** Full logout that also clears tenant — goes back to instance select */
  logoutAndSwitchInstance(): void {
    this.http.post(`${this.base}/v1/tenant/logout`, {}).subscribe({ error: () => {} });
    this.clearAuth();
    this.tenant.clearTenant();
    this.router.navigate(['/select-instance']);
  }

  // ─── Profile ───────────────────────────────────────
  getProfile(): Observable<TenantUser> {
    return this.http.get<{ data: TenantUser }>(`${this.base}/v1/tenant/profile`).pipe(
      map(res => res.data),
      tap(user => { localStorage.setItem('hpm_user', JSON.stringify(user)); this.userSubject.next(user); })
    );
  }

  updateProfile(data: ProfileUpdateRequest): Observable<TenantUser> {
    return this.http.put<{ data: TenantUser }>(`${this.base}/v1/tenant/profile`, data).pipe(
      map(res => res.data),
      tap(user => { localStorage.setItem('hpm_user', JSON.stringify(user)); this.userSubject.next(user); })
    );
  }

  updatePassword(data: PasswordUpdateRequest): Observable<any> {
    return this.http.put(`${this.base}/v1/tenant/profile/password`, data);
  }

  updateLogo(file: File): Observable<TenantUser> {
    const fd = new FormData();
    fd.append('logo', file);
    return this.http.post<{ data: TenantUser }>(`${this.base}/v1/tenant/profile/logo`, fd).pipe(
      map(res => res.data),
      tap(user => { localStorage.setItem('hpm_user', JSON.stringify(user)); this.userSubject.next(user); })
    );
  }

  // ─── Phone OTP Login ────────────────────────────────
  // POST /v1/tenant/login-by-phone  — sends OTP to phone
  sendOtp(phone: string): Observable<any> {
    return this.http.post(`${this.base}/v1/tenant/login-by-phone`, { phone });
  }

  // POST /v1/tenant/verify-phone-login  — verifies OTP, returns token
  verifyOtp(phone: string, code: string): Observable<any> {
    return this.http.post<any>(`${this.base}/v1/tenant/verify-phone-login`, { phone, code }).pipe(
      tap(res => {
        console.log('[AuthService] Phone login response:', JSON.stringify(res).substring(0, 500));
        const payload = res.data || res;
        const token = payload.token || payload.access_token || res.token || res.access_token;
        const user = payload.user || res.user;
        console.log('[AuthService] Extracted token:', token ? token.substring(0, 20) + '...' : 'NONE');
        if (token && token !== 'undefined') {
          this.storeAuth(token, user || { name: 'User', phone });
        } else {
          console.error('[AuthService] No token found in phone login response!', res);
        }
      }),
      catchError(err => { return throwError(() => err); })
    );
  }

  // ─── Forgot / Reset Password ───────────────────────
  // POST /v1/tenant/forgot-password
  forgotPassword(email: string): Observable<any> {
    const fd = new FormData();
    fd.append('email', email);
    return this.http.post(`${this.base}/v1/tenant/forgot-password`, fd);
  }

  // POST /v1/tenant/reset-password
  resetPassword(data: { token: string; email: string; password: string; password_confirmation: string }): Observable<any> {
    return this.http.post(`${this.base}/v1/tenant/reset-password`, data);
  }
}
